import { NarrativeMiniPriceChart } from '@/components/MiniPriceChart/components/MiniPriceChart/NarrativeMiniPriceChart';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart/components/MiniPriceChartDefinitions/MiniPriceChartDefinitions';
import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { formatDate } from 'date-fns';
import { useCallback, useState } from 'react';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount/CompanyIntrinsicDiscount';
import { PositiveNegativeValue } from './components/PositiveNegativeValue';
import type { FragmentOf } from 'gql.tada';
import type { FAIR_VALUE_HISTORY_GRAPH_NARRATIVE_FRAGMENT } from './narrativeFragment';

const CHART_HEIGHT = 68;

type Props = {
  narrative: FragmentOf<typeof FAIR_VALUE_HISTORY_GRAPH_NARRATIVE_FRAGMENT>;
};

export const FairValueHistoryGraph = ({ narrative }: Props) => {
  const companyCurrencyISO =
    getTradingItemCurrencyISO(narrative.company?.data.currencyInfo) ?? 'XXX';

  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [selectedFairValue, setSelectedFairValue] = useState<number | null>(
    null
  );

  const onHover = useCallback(({ time, value, fairValue }) => {
    setSelectedTime(time);
    setSelectedPrice(value);
    setSelectedFairValue(fairValue);
  }, []);

  const fairValue = selectedFairValue ?? narrative.valuation?.fairValue ?? null;
  const valuations = narrative?.valuations ?? [];
  const updatedAt =
    selectedTime ?? narrative.updatedAt ?? new Date().toISOString();
  const { analysisValue } = narrative.company ?? {};
  const lastSharePrice = selectedPrice ?? analysisValue?.lastSharePrice ?? null;
  const return1yAbs = analysisValue?.return1yAbs ?? null;
  const return7d = analysisValue?.return7d ?? null;
  const npvPerShare = analysisValue?.npvPerShare ?? null;

  return (
    <div
      className="border relative h-x20 rounded-md border-subtle bg-p-grey-100/70 shadow-sm light:bg-p-white-110/70 light:shadow-sm lg:h-[200px]"
      role="figure"
      aria-label="Share price with fair value chart"
    >
      <div className="h-x20 p-3 lg:h-[200px] lg:px-4 lg:py-5">
        <MiniPriceChartDefinitions />
        <div className="grid grid-cols-[1fr_.9fr] gap-2 lg:mb-2 lg:grid-cols-[1fr_1.5fr]">
          <div className="grid grid-cols-1 gap-1">
            <div className="flex items-center gap-1 text-xs text-soft">
              <div className="size-1.5 min-w-x0_75 rounded-full bg-chart-03" />
              <span className="flex min-w-0 items-center">
                <span className="truncate">
                  {narrative.owner.displayName || 'User'}
                </span>
                <span className="whitespace-nowrap">&apos;s Fair Value</span>
              </span>
            </div>
            <div className="flex items-center gap-2 pl-2.5">
              <div className="text-sm text-solid">
                {formatterCurrency(fairValue, companyCurrencyISO)}
              </div>
              <CompanyIntrinsicDiscount
                npvPerShare={npvPerShare}
                lastSharePrice={lastSharePrice}
                fairValue={fairValue}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1">
            <div className="flex items-center gap-1 text-xs text-soft">
              <div className="size-1.5 rounded-full bg-good" />
              <div className="flex-1">
                {formatDate(new Date(updatedAt), 'dd MMM')}
              </div>
            </div>
            <div className="flex items-center pl-2.5">
              <div className="text-sm text-solid">
                {formatterCurrency(lastSharePrice, companyCurrencyISO)}
              </div>
            </div>
          </div>
        </div>
        <NarrativeMiniPriceChart
          companyId={narrative.companyId}
          period="1Y"
          height={CHART_HEIGHT}
          interactive={true}
          valuations={valuations}
          onHover={onHover}
          currencyISO={companyCurrencyISO}
          baselineType="average"
        />
        <div className="mt-2 grid grid-cols-[1fr_5fr] gap-2">
          <div className="flex items-center gap-2">
            <div className="text-xs text-soft">1Y</div>
            <PositiveNegativeValue value={return1yAbs}>
              {formatterPercentage(return1yAbs)}
            </PositiveNegativeValue>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-soft">7D</div>
            <PositiveNegativeValue value={return7d}>
              {formatterPercentage(return7d)}
            </PositiveNegativeValue>
          </div>
        </div>
      </div>
    </div>
  );
};
